import React, { Dispatch, SetStateAction, useState, useMemo } from "react";
import { Modal } from "./Modal";
import { HeaderModal } from "./Header";
import { FooterModal } from "./Footer";
import close from "assets/images/close-icon.svg";
import { Body } from "./Body";
import { updateVijilization } from "services/project";
import { InputTextField } from "components/InputField";
import Toast from "../Toast";
import allImgPaths from "assets/images";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import { getEditVijilizationSteps } from "utils/walkthroughs";

interface Iprops {
    isShow: boolean;
    setIsShow: Dispatch<SetStateAction<boolean>>;
    setRefreshData: () => void;
    vijilizationId: string;
    vijilizationName: string;
    vijilizationDescription?: string;
    vijilizationStatus?: string;
    onAddMore?: () => void;
}

const EditVijilization = ({
    isShow,
    setIsShow,
    setRefreshData,
    vijilizationId,
    vijilizationName,
    vijilizationDescription = "",
    vijilizationStatus,
    onAddMore,
}: Iprops) => {
    const [name, setName] = useState<string>(vijilizationName);
    const [description, setDescription] = useState<string>(
        vijilizationDescription
    );

    // Validation check for vijilization name
    const isVijilizationNameValid = useMemo(() => {
        return name.trim().length > 0;
    }, [name]);

    const onCloseModal = () => {
        setIsShow(false);
        setName(vijilizationName); // Reset to original name on close
        setDescription(vijilizationDescription); // Reset to original description on close
    };

    // Whether the "Add More & Re-Vijilize" action is available in the footer;
    // the walkthrough includes a step for it only when it is shown.
    const showAddMore = vijilizationStatus === "completed" && !!onAddMore;

    const startWalkthrough = () => {
        driver({
            showProgress: true,
            steps: getEditVijilizationSteps(showAddMore),
        }).drive();
    };

    const handleVijilizationNameChange = (e: any) => {
        setName(e.target.value);
    };

    const handleVijilizationDescriptionChange = (e: any) => {
        setDescription(e.target.value);
    };

    const handleSubmit = async (event: any) => {
        event?.preventDefault();

        if (!isVijilizationNameValid) {
            return;
        }

        try {
            setIsShow(false);
            await updateVijilization(
                vijilizationId,
                name.trim(),
                description.trim()
            );
            setRefreshData();
            Toast.success("Success", "Vijilization updated successfully");
        } catch (error) {
            console.error("Error updating vijilization:", error);
            Toast.error(
                "Error",
                "Failed to update vijilization. Please try again."
            );
        }
    };

    return (
        <>
            <Modal className={"modal-wrapper max-w-[580px]"} isShow={isShow}>
                <form onSubmit={handleSubmit}>
                    <HeaderModal
                        title={"test"}
                        isShowHeader={true}
                        className="modal-header pt-6 px-6"
                    >
                        <div className="flex items-center justify-between pb-3">
                            <img
                                className="h-10 w-10"
                                src={allImgPaths.editModal2}
                                alt="edit"
                            />
                            <div className="flex items-center gap-x-1 mb-4">
                                <button
                                    type="button"
                                    onClick={startWalkthrough}
                                    className="cursor-pointer hover:bg-[#F5F8FD] text-[#4F4D55] hover:text-[#1751D0] rounded p-1"
                                    aria-label="Show guided walkthrough"
                                    title="Show guided walkthrough"
                                >
                                    <HelpOutlineIcon sx={{ fontSize: 20 }} />
                                </button>
                                <img
                                    className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                                    id="modal-toggle"
                                    src={close}
                                    onClick={onCloseModal}
                                    alt="close icon"
                                />
                            </div>
                        </div>

                        <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
                            Edit Vijilization
                        </h2>
                        <p className="text-sm text-[#4F4D55] font-interRegular">
                            Edit/Update the name of vijilization
                        </p>
                    </HeaderModal>

                    <Body
                        title=""
                        isShowBody={true}
                        className="modal-body px-6 pt-7"
                    >
                        <div className="pb-7" id="edit-vijilization-name">
                            <label
                                htmlFor=""
                                className="text-[#2D2B32] text-sm font-interMedium block pb-2"
                            >
                                Vijilization Name
                            </label>
                            <InputTextField
                                className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full"
                                name={"name"}
                                placeholder={"Enter Vijilization Name"}
                                onChange={handleVijilizationNameChange}
                                value={name}
                            />
                        </div>
                        <div className="pb-7" id="edit-vijilization-description">
                            <label
                                htmlFor=""
                                className="text-[#2D2B32] text-sm font-interMedium block pb-2"
                            >
                                Description
                                <span className="text-xs text-[#7F7D83] ml-2">
                                    Optional
                                </span>
                            </label>
                            <textarea
                                className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full border border-[#D0D5DD] rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#1751D0] focus:border-transparent"
                                name="description"
                                placeholder="Enter description"
                                rows={3}
                                onChange={handleVijilizationDescriptionChange}
                                value={description}
                            />
                        </div>
                    </Body>

                    <FooterModal
                        title=""
                        isShowFooter={true}
                        className="modal-footer pt-5 px-6 pb-6"
                    >
                        {vijilizationStatus === "completed" && onAddMore && (
                            <div className="pb-4">
                                <button
                                    type="button"
                                    id="edit-vijilization-add-more"
                                    onClick={() => {
                                        onAddMore();
                                        setIsShow(false);
                                    }}
                                    className="w-full flex items-center justify-center gap-x-2 text-[#1751D0] font-interSemiBold border border-[#1751D0] rounded-lg tracking-wide py-2.5 px-2.5 !text-base hover:bg-[#F5F8FD]"
                                >
                                    <img
                                        src={allImgPaths.plusIcon}
                                        alt="add"
                                        className="w-4 h-4"
                                    />
                                    Add More & Re-Vijilize
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-x-3 justify-center">
                            <button
                                id="edit-vijilization-cancel"
                                onClick={onCloseModal}
                                type="button"
                                className="text-[#4F4D55] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                            >
                                Cancel
                            </button>
                            <button
                                id="edit-vijilization-update"
                                className={`!text-base text-white font-interSemiBold bg-[#1751D0] hover:bg-[#113B98] rounded-lg px-3.5 py-2.5 flex-1 ${
                                    isVijilizationNameValid
                                        ? ""
                                        : "button-disabled"
                                }`}
                                type="submit"
                                disabled={!isVijilizationNameValid}
                            >
                                Update
                            </button>
                        </div>
                    </FooterModal>
                </form>
            </Modal>
        </>
    );
};

export default EditVijilization;
